import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from '../ui/Card';
import { useTodos, TodoItem } from '../../hooks/useTodos';
import { Check, Plus, Trash2, ExternalLink, GripVertical } from 'lucide-react';
const PRIORITY_COLORS = {
  high: 'bg-red-500',
  medium: 'bg-amber-500',
  low: 'bg-emerald-500'
};
const PRIORITY_LABELS = {
  high: 'Alta',
  medium: 'Media',
  low: 'Baja'
};
export function TodoListWidget() {
  const {
    todos,
    addTodo,
    toggleTodo,
    removeTodo
  } = useTodos();
  const [filter, setFilter] = useState<'all' | 'active' | 'completed'>('all');
  const [newTaskText, setNewTaskText] = useState('');
  const [newTaskPriority, setNewTaskPriority] = useState<'high' | 'medium' | 'low'>('medium');
  const filteredTodos = todos.filter(todo => {
    if (filter === 'active') return !todo.completed;
    if (filter === 'completed') return todo.completed;
    return true;
  });
  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTaskText.trim()) return;
    addTodo({
      text: newTaskText,
      completed: false,
      priority: newTaskPriority,
      color: '#6366f1',
      category: 'General'
    });
    setNewTaskText('');
  };
  return <Card className="h-full bg-slate-900 border-slate-800 flex flex-col max-h-[400px]" data-id="element-3214">
      <div className="p-4 border-b border-slate-800 flex items-center justify-between shrink-0" data-id="element-3215">
        <h3 className="text-sm font-semibold text-slate-100 flex items-center gap-2" data-id="element-3216">
          <span className="w-2 h-2 rounded-full bg-indigo-500" data-id="element-3217"></span>
          Mis Tareas
        </h3>
        <div className="flex items-center gap-1 bg-slate-950 p-1 rounded-lg border border-slate-800" data-id="element-3218">
          {(['all', 'active', 'completed'] as const).map(f => <button key={f} onClick={() => setFilter(f)} className={`px-2.5 py-1 text-[10px] font-medium rounded-md transition-colors ${filter === f ? 'bg-slate-800 text-slate-200' : 'text-slate-500 hover:text-slate-300'}`} data-id="element-3219">
              {f === 'all' ? 'Todas' : f === 'active' ? 'Activas' : 'Completadas'}
            </button>)}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-2 custom-scrollbar" data-id="element-3220">
        <AnimatePresence initial={false} data-id="element-3221">
          {filteredTodos.map(todo => <motion.div key={todo.id} initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.2
        }} className="group flex items-start gap-3 p-2 hover:bg-slate-800/50 rounded-lg transition-colors" data-id="element-3222">
              <button className="mt-1 text-slate-600 cursor-grab hover:text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-3223">
                <GripVertical size={14} data-id="element-3224" />
              </button>

              <button onClick={() => toggleTodo(todo.id)} className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${todo.completed ? 'bg-indigo-500 border-indigo-500 text-white' : 'border-slate-600 hover:border-indigo-400'}`} data-id="element-3225">
                {todo.completed && <Check size={10} data-id="element-3226" />}
              </button>

              <div className="flex-1 min-w-0" data-id="element-3227">
                <div className="flex items-start gap-2" data-id="element-3228">
                  <p className={`text-sm transition-all ${todo.completed ? 'text-slate-500 line-through' : 'text-slate-200'}`} data-id="element-3229">
                    {todo.text}
                  </p>
                  {todo.link && <a href={todo.link} target="_blank" rel="noreferrer" className="text-indigo-400 hover:text-indigo-300 mt-0.5 shrink-0" data-id="element-3230">
                      <ExternalLink size={12} data-id="element-3231" />
                    </a>}
                </div>
                <div className="flex items-center gap-2 mt-1" data-id="element-3232">
                  <span className="flex items-center gap-1 text-[10px] text-slate-500" data-id="element-3233">
                    <span className={`w-1.5 h-1.5 rounded-full ${PRIORITY_COLORS[todo.priority]}`} data-id="element-3234"></span>
                    {PRIORITY_LABELS[todo.priority]}
                  </span>
                  <span className="text-[10px] text-slate-600 px-1.5 py-0.5 bg-slate-950 rounded border border-slate-800" data-id="element-3235">
                    {todo.category}
                  </span>
                </div>
              </div>

              <button onClick={() => removeTodo(todo.id)} className="text-slate-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity p-1" data-id="element-3236">
                <Trash2 size={14} data-id="element-3237" />
              </button>
            </motion.div>)}
        </AnimatePresence>

        {filteredTodos.length === 0 && <div className="text-center py-8 text-slate-500 text-xs" data-id="element-3238">
            No hay tareas {filter !== 'all' ? 'en esta vista' : ''}
          </div>}
      </div>

      <div className="p-3 border-t border-slate-800 bg-slate-900/50 shrink-0" data-id="element-3239">
        <form onSubmit={handleAdd} className="flex items-center gap-2" data-id="element-3240">
          <input type="text" value={newTaskText} onChange={e => setNewTaskText(e.target.value)} placeholder="Nueva tarea..." className="flex-1 bg-slate-950 border border-slate-800 rounded-lg px-3 py-1.5 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-indigo-500" data-id="element-3241" />

          <select value={newTaskPriority} onChange={e => setNewTaskPriority(e.target.value as any)} className="bg-slate-950 border border-slate-800 rounded-lg px-2 py-1.5 text-xs text-slate-400 focus:outline-none focus:border-indigo-500" data-id="element-3242">
            <option value="low" data-id="element-3243">Baja</option>
            <option value="medium" data-id="element-3244">Media</option>
            <option value="high" data-id="element-3245">Alta</option>
          </select>
          <button type="submit" disabled={!newTaskText.trim()} className="p-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-3246">
            <Plus size={16} data-id="element-3247" />
          </button>
        </form>
      </div>
    </Card>;
}